import { CostCodes, Data, UI, Utils } from 'common';
import difference from 'lodash/difference';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import React, { Component } from 'react';

import ContactsDetailsHeader from './ContactsDetailsHeader.component';
import ManageGroupsModal from './groups/ManageGroupsModal.component';
import Contact from './utils/Contact';
import ContactsCommunication from './utils/ContactsCommunication';
import Util from './utils/Util';

const {
	WrappedInputTextWithLabel,
	WrappedDropdownWithLabel,
	WrappedInputTextareaWithLabel,
	WrappedMultiSelectWithLabel,
	CollapsibleInput,
} = UI;
const { withLayoutAndModalContexts, Validation, Session } = Utils;
const { Countries, States, NamePrefixes, InternalCapability } = Data;
const { ManageCostCodesModal } = CostCodes;

class SingleContactEditView extends Component {
	constructor(props) {
		super(props);

		const { contact } = this.props;
		const contactToEdit = SingleContactEditView.normalizePostalCode(
			contact || Contact()
		);

		this.state = {
			contactToEdit,
			selectedGroups: [],
			costCodeOptions: [],
			isAddressValid: true,
			isEmailValid: true,
		};

		// used for Logging
		this.changedFields = {};

		// used for groupAssociation API call
		this.initialAssociatedGroups = [];
	}

	componentDidMount() {
		this.getContactData();
		this.buildCostCodeOptions();
	}

	componentDidUpdate(prevProps) {
		const { contact, refreshGroupUpdate, costCodes, groups } = this.props;

		if (
			!isEqual(prevProps.contact, contact) ||
			!isEqual(prevProps.refreshGroupUpdate, refreshGroupUpdate)
		) {
			this.refreshContactData(contact);
		} else if (!isEqual(prevProps.costCodes, costCodes)) {
			this.buildCostCodeOptions();
		} else if (!isEqual(prevProps.groups, groups)) {
			this.updateSelectedGroupsData(groups);
		}
	}

	// Normalize PostalCode from Zip when missing (e.g. imported contacts may have Zip only)
	static normalizePostalCode(contact) {
		if (!contact) return contact;
		const hasPostalCode =
			contact.PostalCode && contact.PostalCode.trim() !== '';
		if (hasPostalCode) return { ...contact };
		const zipDisplay =
			contact.Zip &&
			contact.Zip.trim() !== '' &&
			contact.ZipAddOn &&
			contact.ZipAddOn.trim() !== ''
				? `${contact.Zip}-${contact.ZipAddOn}`
				: contact.Zip || '';
		return { ...contact, PostalCode: zipDisplay };
	}

	// refresh contact data if contact data has changed
	refreshContactData = contact => {
		this.setState(
			{
				contactToEdit: SingleContactEditView.normalizePostalCode(contact),
				selectedGroups: [],
			},
			this.getContactData
		);
	};

	handleInputChange = ({ target: { name, value } }) => {
		const { contactToEdit } = this.state;
		const {
			groups,
			fetchContacts,
			modalContext: { showModal },
		} = this.props;

		if (name === 'selectedGroups') {
			if (value.find(groupObject => groupObject.id === -1)) {
				showModal({
					component: ManageGroupsModal,
					data: {
						groups,
						callBack: fetchContacts,
					},
				});
			} else {
				this.setState(
					{
						selectedGroups: value,
					},
					this.groupAssociation
				);
			}
		} else {
			this.setState({
				contactToEdit: {
					...contactToEdit,
					[name]: value,
				},
			});
		}

		this.changedFields = {
			...this.changedFields,
			[name]: value,
		};
	};

	// handles DropDown components saving to state and API call
	onChangeDropdown = async ({ target: { name, value } }) => {
		const { contactToEdit } = this.state;
		const {
			costCodes,
			getCostCodes,
			modalContext: { showModal },
		} = this.props;

		// show ManageCostCodes modal
		if (value === -2) {
			showModal({
				component: ManageCostCodesModal,
				data: {
					callback: getCostCodes,
				},
			});

			return;
		}

		// builds fullName field when prefix is changed
		const newFullName =
			name === 'Prefix'
				? Util.getContactFullName(value, contactToEdit)
				: contactToEdit.FullName;

		const newContactToEdit = {
			...contactToEdit,
			[name]: value,
			FullName: newFullName,
		};

		// sets the costCode name based on costCodeId
		if (name === 'CostCodeId') {
			newContactToEdit.CostCode = find(costCodes, ['id', value])?.name;
			await this.costCodeAssociation(value);
		}

		// sets countryCode based of CountryId
		if (name === 'CountryId') {
			newContactToEdit.CountryCode = find(Countries, ['id', value]).countryCode;
		}

		this.setState(
			{
				contactToEdit: newContactToEdit,
			},
			this.saveContactData
		);

		this.changedFields = {
			...this.changedFields,
			[name]: value,
			FullName: newFullName,
		};
	};

	// handles email validation and then calls onInputTextBlur to make API call
	validateEmailInput = ({ target: { value } }) => {
		const isEmailValid = Validation.isValidEmail(value);

		this.setState(
			{
				isEmailValid,
			},
			() => {
				if (isEmailValid) {
					this.saveContactData();
				}
			}
		);
	};

	validateAddressInput = ({ target: { value } }) => {
		const isAddressValid = Validation.isValidAddress(value);

		this.setState(
			{
				isAddressValid,
			},
			() => {
				if (isAddressValid) {
					this.saveContactData();
				}
			}
		);
	};

	onInputTextBlur = ({ target: { value } }) => {
		if (value !== '') {
			this.saveContactData();
		}
	};

	saveContactData = () => {
		const { contactToEdit } = this.state;
		const { contact, updateContact, addContact } = this.props;

		if (!isEqual(contact, contactToEdit)) {
			if (contactToEdit.ContactId === null) {
				addContact(contactToEdit);
			} else {
				updateContact(contactToEdit, this.changedFields);
			}

			// reset logging changedFields object after saving
			this.changedFields = {};
		}
	};

	parseName = async ({ target: { value } }) => {
		const { contactToEdit } = this.state;
		const {
			modalContext: { showError },
		} = this.props;

		if (value !== '') {
			const response = await ContactsCommunication.parseName({
				fullName: value,
			});

			if (ContactsCommunication.isSuccessful(response)) {
				const { Prefix, FirstName, MiddleName, LastName, Suffix } =
					response.data;

				contactToEdit.Prefix = Prefix;
				contactToEdit.FirstName = FirstName;
				contactToEdit.MiddleName = MiddleName;
				contactToEdit.LastName = LastName;
				contactToEdit.Suffix = Suffix;

				this.setState(
					{
						contactToEdit,
					},
					this.saveContactData
				);
			} else {
				showError({ response });
			}
		}
	};

	// calls getContactData endpoint to retrieve costCode and group data
	getContactData = async () => {
		const { contactToEdit } = this.state;
		const {
			modalContext: { showError },
		} = this.props;

		if (!contactToEdit.ContactId) {
			return;
		}

		const response = await ContactsCommunication.getContactData(
			contactToEdit.ContactId
		);

		if (ContactsCommunication.isSuccessful(response)) {
			const { CostCode, Groups } = response.data;

			this.initialAssociatedGroups = Groups;

			this.setState({
				selectedGroups: Groups,
				contactToEdit: {
					...contactToEdit,
					CostCode: CostCode.name,
					CostCodeId: CostCode && CostCode.id === 0 ? -1 : CostCode.id,
				},
			});
		} else {
			showError({ response });
		}
	};

	groupAssociation = async () => {
		const { contactToEdit, selectedGroups } = this.state;
		const {
			fetchContacts,
			modalContext: { showError },
		} = this.props;

		const data = {
			associate: selectedGroups.map(group => group.id),
			disassociate: difference(
				this.initialAssociatedGroups,
				selectedGroups
			).map(group => group.id),
			allSelected: false,
			newGroups: [],
			contactId:
				contactToEdit.ContactId === null
					? await this.getNewContactId()
					: contactToEdit.ContactId,
		};

		const response = await ContactsCommunication.groupAssociations(data);

		if (ContactsCommunication.isSuccessful(response)) {
			fetchContacts();
		} else {
			showError({ response });
		}
	};

	referenceAssociation = async ({ target: { value } }) => {
		const { contactToEdit } = this.state;
		const {
			modalContext: { showError },
		} = this.props;

		if (value !== '') {
			const data = {
				reference: value,
				allSelected: false,
				contactId:
					contactToEdit.ContactId === null
						? await this.getNewContactId()
						: contactToEdit.ContactId,
			};

			const response = await ContactsCommunication.referenceAssociations(data);

			if (ContactsCommunication.isSuccessful(response)) {
				this.saveContactData();
			} else {
				showError({ response });
			}
		}
	};

	costCodeAssociation = async costCodeId => {
		const { contactToEdit } = this.state;
		const {
			fetchContacts,
			modalContext: { showError },
		} = this.props;

		const data = {
			costCodeId,
			allSelected: false,
			contactId:
				contactToEdit.ContactId === null
					? await this.getNewContactId()
					: contactToEdit.ContactId,
		};

		const response = await ContactsCommunication.costCodeAssociations(data);

		if (ContactsCommunication.isSuccessful(response)) {
			fetchContacts();
		} else {
			showError({ response });
		}
	};

	// get ContactId in the case a user try's adding a new group, cost code, or reference number before any other input fields
	getNewContactId = async () => {
		const { contactToEdit } = this.state;
		const { addContact } = this.props;

		const newContact = await addContact(contactToEdit);
		return newContact.ContactId;
	};

	selectedGroupsTemplate = groupObject =>
		groupObject ? (
			<span className="multiselect-selected-item-template">
				<span className="name">{groupObject.name}</span>
				<i
					aria-label="Remove Tag"
					id={groupObject.id}
					role="button"
					tabIndex={0}
					className="remove pi pi-times"
					onClick={this.onClickRemoveGroup}
				/>
			</span>
		) : null;

	// removes group name from MultiSelect
	onClickRemoveGroup = e => {
		e.stopPropagation();
		const { selectedGroups } = this.state;

		this.setState(
			{
				selectedGroups: selectedGroups.filter(
					group => group.id !== Number(e.target.id)
				),
			},
			this.groupAssociation
		);
	};

	// adds/removes groups based on selectedGroup data
	filterGroupsDropDownData = () => {
		const { selectedGroups } = this.state;
		const { groups } = this.props;

		const result = groups.filter(
			group =>
				!selectedGroups.some(selectedGroup => group.id === selectedGroup.id) &&
				group.id !== -1 // remove 'Ungrouped' from dropdown
		);

		// add button to trigger ManageGroups modal
		result.unshift({
			id: -1,
			name: 'Add/Edit Groups',
			dataqa: 'groups-dropdown-add-edit-groups',
		});

		return result;
	};

	updateSelectedGroupsData = updatedGroups => {
		const { selectedGroups } = this.state;

		const result = updatedGroups.filter(group =>
			selectedGroups.some(selectedGroup => group.id === selectedGroup.id)
		);

		this.setState({ selectedGroups: result });
	};

	buildCostCodeOptions = () => {
		const { costCodes } = this.props;
		const tempCostCodes = [...costCodes];

		// add button to trigger ManageCostCodes modal

		if (Session.hasCapability(InternalCapability.EditCostCodes)) {
			tempCostCodes.unshift({ id: -2, name: 'Add/Edit Cost Codes' });
		}

		this.setState({ costCodeOptions: tempCostCodes });
	};

	render() {
		const {
			contactToEdit,
			isEmailValid,
			isAddressValid,
			selectedGroups,
			costCodeOptions,
		} = this.state;
		const {
			selectedContacts,
			deleteContacts,
			onPrintPostageClick,
			layoutContext: { isMobile },
		} = this.props;

		return (
			<div className="contacts-details">
				<ContactsDetailsHeader
					headerText={contactToEdit.FullName || 'Contact Details'}
					selectedContacts={selectedContacts}
					deleteContacts={deleteContacts}
					onPrintPostageClick={onPrintPostageClick}
				/>
				<div className="p-grid">
					<CollapsibleInput
						className="p-col-12"
						label="Full Name:"
						value={contactToEdit.FullName}
						name="FullName"
						dataqa="fullname"
						maxLength="50"
						onChange={this.handleInputChange}
						onBlur={this.parseName}
					>
						<WrappedDropdownWithLabel
							className="name-details dropdown p-col-12"
							label="Prefix:"
							uiElementConfig={{
								value: contactToEdit.Prefix,
								name: 'Prefix',
								options: NamePrefixes,
								placeholder: '-None-',
								dataqa: 'prefix',
								onChange: this.onChangeDropdown,
							}}
						/>
						<WrappedInputTextWithLabel
							className="name-details inputtext p-col-12"
							label="First Name:"
							uiElementConfig={{
								type: 'text',
								value: contactToEdit.FirstName,
								name: 'FirstName',
								dataqa: 'firstname',
								maxLength: 50,
								onChange: this.handleInputChange,
								onBlur: this.onInputTextBlur,
							}}
						/>
						<WrappedInputTextWithLabel
							className="name-details inputtext p-col-12"
							label="Middle Name:"
							uiElementConfig={{
								type: 'text',
								value: contactToEdit.MiddleName,
								name: 'MiddleName',
								dataqa: 'middlename',
								maxLength: 20,
								onChange: this.handleInputChange,
								onBlur: this.onInputTextBlur,
							}}
						/>
						<WrappedInputTextWithLabel
							className="name-details inputtext p-col-12"
							label="Last Name:"
							uiElementConfig={{
								type: 'text',
								value: contactToEdit.LastName,
								name: 'LastName',
								dataqa: 'lastname',
								maxLength: 50,
								onChange: this.handleInputChange,
								onBlur: this.onInputTextBlur,
							}}
						/>
						<WrappedInputTextWithLabel
							className="name-details inputtext p-col-12"
							label="Suffix:"
							uiElementConfig={{
								type: 'text',
								value: contactToEdit.Suffix,
								name: 'Suffix',
								dataqa: 'suffix',
								maxLength: 20,
								onChange: this.handleInputChange,
								onBlur: this.onInputTextBlur,
							}}
						/>
					</CollapsibleInput>

					<CollapsibleInput
						className="p-col-12"
						label="Company:"
						value={contactToEdit.Company}
						name="Company"
						dataqa="company"
						maxLength="50"
						onChange={this.handleInputChange}
						onBlur={this.onInputTextBlur}
					>
						<WrappedInputTextWithLabel
							className="company-details inputtext p-col-12"
							label="Department:"
							uiElementConfig={{
								type: 'text',
								value: contactToEdit.Department,
								name: 'Department',
								dataqa: 'department',
								maxLength: 50,
								onChange: this.handleInputChange,
								onBlur: this.onInputTextBlur,
							}}
						/>
						<WrappedInputTextWithLabel
							className="company-details inputtext p-col-12"
							label="Title:"
							uiElementConfig={{
								type: 'text',
								value: contactToEdit.Title,
								name: 'Title',
								dataqa: 'title',
								maxLength: 35,
								onChange: this.handleInputChange,
								onBlur: this.onInputTextBlur,
							}}
						/>
					</CollapsibleInput>
					<WrappedDropdownWithLabel
						className="dropdown p-col-12"
						label="Country:"
						uiElementConfig={{
							value: contactToEdit.CountryId,
							name: 'CountryId',
							options: Countries,
							optionLabel: 'countryName',
							optionValue: 'id',
							dataqa: 'country',
							onChange: this.onChangeDropdown,
						}}
					/>
					<WrappedInputTextareaWithLabel
						className="inputtextarea p-col-12"
						label="Street Address:"
						showErrorBelow={isMobile}
						isInputInvalid={!isAddressValid}
						errorMessage="Maximum 3 lines with limit of 50 chars in each"
						uiElementConfig={{
							rows: 3,
							cols: 41,
							value: contactToEdit.Street1,
							name: 'Street1',
							dataqa: 'streetaddress',
							maxLength: 70,
							onChange: this.handleInputChange,
							onBlur: this.validateAddressInput,
						}}
					/>
					<WrappedInputTextWithLabel
						className="inputtext p-col-12"
						label="City:"
						uiElementConfig={{
							type: 'text',
							value: contactToEdit.City,
							name: 'City',
							dataqa: 'city',
							maxLength: 50,
							onChange: this.handleInputChange,
							onBlur: this.onInputTextBlur,
						}}
					/>
					{contactToEdit.CountryId === 203 ? (
						<WrappedDropdownWithLabel
							className="dropdown p-col-12"
							label="State:"
							uiElementConfig={{
								value: contactToEdit.State,
								name: 'State',
								options: States,
								optionLabel: 'name',
								optionValue: 'abbreviation',
								dataqa: 'state',
								onChange: this.onChangeDropdown,
							}}
						/>
					) : (
						<WrappedInputTextWithLabel
							className="inputtext p-col-12"
							label="Province:"
							uiElementConfig={{
								type: 'text',
								value: contactToEdit.Province,
								name: 'Province',
								dataqa: 'province',
								maxLength: 50,
								onChange: this.handleInputChange,
								onBlur: this.onInputTextBlur,
							}}
						/>
					)}
					<WrappedInputTextWithLabel
						className="inputtext p-col-12"
						label={
							contactToEdit.CountryId === 203 ? 'Zip Code' : 'Postal Code:'
						}
						uiElementConfig={{
							type: 'text',
							value: contactToEdit.PostalCode,
							name: 'PostalCode',
							dataqa: 'postalcode',
							maxLength: 50,
							onChange: this.handleInputChange,
							onBlur: this.onInputTextBlur,
						}}
					/>
					<WrappedInputTextWithLabel
						showErrorBelow={isMobile}
						isInputInvalid={!isEmailValid}
						errorMessage="Please enter a valid email address"
						className="inputtext p-col-12"
						label="Email:"
						uiElementConfig={{
							type: 'text',
							value: contactToEdit.Email,
							name: 'Email',
							dataqa: 'email',
							maxLength: 255,
							onChange: this.handleInputChange,
							onBlur: this.validateEmailInput,
						}}
					/>
					<WrappedInputTextWithLabel
						className="inputtext p-col-9"
						label="Phone:"
						uiElementConfig={{
							type: 'text',
							value: contactToEdit.Phone,
							name: 'Phone',
							dataqa: 'phone',
							maxLength: 14,
							onChange: this.handleInputChange,
							onBlur: this.onInputTextBlur,
							onKeyPress: Validation.acceptOnlyNumbersInput,
							chartoaccept: 45,
						}}
					/>
					<WrappedInputTextWithLabel
						className="phone-extension inputtext p-col-3"
						label="X:"
						uiElementConfig={{
							type: 'text',
							value: contactToEdit.PhoneExt,
							name: 'PhoneExt',
							dataqa: 'phoneext',
							maxLength: 5,
							onChange: this.handleInputChange,
							onBlur: this.onInputTextBlur,
							onKeyPress: Validation.acceptOnlyNumbersInput,
							chartoaccept: 45,
						}}
					/>
					<WrappedMultiSelectWithLabel
						className="multiselect p-col-12"
						label="Groups:"
						uiElementConfig={{
							value: selectedGroups,
							name: 'selectedGroups',
							options: this.filterGroupsDropDownData(),
							optionLabel: 'name',
							placeholder: 'Select groups...',
							filter: true,
							filterPlaceholder: 'Search',
							dataqa: 'groups',
							onChange: this.handleInputChange,
							selectedItemTemplate: this.selectedGroupsTemplate,
						}}
					/>
					<WrappedInputTextWithLabel
						className="inputtext p-col-12"
						label="Reference #:"
						uiElementConfig={{
							type: 'text',
							value: contactToEdit.ReferenceNumber,
							name: 'ReferenceNumber',
							dataqa: 'referencenumber',
							maxLength: 35,
							onChange: this.handleInputChange,
							onBlur: this.referenceAssociation,
						}}
					/>
					<WrappedDropdownWithLabel
						className="dropdown p-col-12"
						label="Cost Code:"
						uiElementConfig={{
							value: contactToEdit.CostCodeId,
							name: 'CostCodeId',
							options: costCodeOptions,
							optionLabel: 'name',
							optionValue: 'id',
							dataqa: 'costcodeid',
							onChange: this.onChangeDropdown,
						}}
					/>
				</div>
			</div>
		);
	}
}

export default withLayoutAndModalContexts(SingleContactEditView);
